import type { FieldValue } from "@/components/shared/UniversalCRMTable/types";
import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { validateInlineEdit } from "./inline-edit-validator";

/**
 * Result of an inline edit operation
 */
export interface InlineEditResult {
  success: boolean;
  error?: string;
  sanitizedValue?: FieldValue;
}

/**
 * Validates an inline edit before it's sent to the mutation
 * Returns sanitized value if valid, error message if invalid
 */
export async function validateAndSanitizeInlineEdit(
  fieldId: string,
  newValue: FieldValue,
  fieldConfig: SimpleFieldConfig
): Promise<InlineEditResult> {
  try {
    // Validate the inline edit
    const validation = validateInlineEdit(fieldId, newValue, fieldConfig);

    if (!validation.isValid) {
      return {
        success: false,
        error: validation.errors.join("; "),
      };
    }

    return {
      success: true,
      sanitizedValue: validation.sanitizedValue,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown validation error";
    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * Creates a typed inline edit handler that validates before calling the mutation
 * This wrapper ensures all inline edits go through validation
 */
export function createValidatedInlineEditHandler(
  onInlineEdit: (item: Record<string, any>) => Promise<void>,
  fieldConfig: SimpleFieldConfig,
  rowData: Record<string, any>
) {
  return async (fieldId: string, newValue: FieldValue): Promise<InlineEditResult> => {
    try {
      // Validate and sanitize the value
      const validation = await validateAndSanitizeInlineEdit(fieldId, newValue, fieldConfig);

      if (!validation.success) {
        return validation;
      }

      // Call the mutation with validated data
      const updatedItem = {
        ...rowData,
        [fieldId]: validation.sanitizedValue,
      };

      await onInlineEdit(updatedItem);

      return {
        success: true,
        sanitizedValue: validation.sanitizedValue,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update field";
      return {
        success: false,
        error: errorMessage,
      };
    }
  };
}
